import { getFlag } from '@mahawi/eshop-common/src/get-flag';
import {
  Button,
  Progress,
  Radio,
  Spin,
  Statistic,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { EDateFormat } from '../../fragments/date-format';
import { type RootState } from '../../reducers';
import { productDashboardLoad } from '../../reducers/product/reducer';
import {
  type IProductDashboardPriceEntry,
  type IProductDashboardScoring,
} from '../../reducers/product/types';
import { COLOR, FONT_SIZE, SPACING } from '../../theme/tokens';
import EntityHistory from '../web-analytics-insights/entity-history';
import WebAnalyticsSection, {
  DAY_OPTIONS,
  type DayInterval,
} from '../web-analytics-section';

const { Text } = Typography;

function PriceChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}): React.ReactElement | null {
  const { token } = theme.useToken();
  if (!active || !payload?.length) {
    return null;
  }
  return (
    <div
      style={{
        background: token.colorBgElevated,
        border: `1px solid ${token.colorBorder}`,
        borderRadius: token.borderRadius,
        padding: `${SPACING.xs}px ${SPACING.sm}px`,
        boxShadow: token.boxShadow,
        fontSize: FONT_SIZE.sm,
        lineHeight: '18px',
      }}
    >
      <div style={{ color: COLOR.primary, fontWeight: 600 }}>
        {(payload[0].value as number).toFixed(2)} EUR
      </div>
      <div style={{ color: token.colorTextSecondary, marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
}

export default function MicroDashboard({
  productUUID,
}: {
  productUUID: string;
}): React.ReactElement {
  const dispatch = useDispatch();
  const dashboard = useSelector((s: RootState) => s.Product.dashboard);
  const inProcess = useSelector((s: RootState) => s.Product.dashboardInProcess);

  const [daysInterval, setDaysInterval] = useState<DayInterval>(30);

  const load = useCallback(
    (interval: DayInterval) => {
      dispatch(productDashboardLoad(productUUID, interval));
    },
    [dispatch, productUUID],
  );

  useEffect(() => {
    load(daysInterval);
  }, [load, daysInterval]);

  const priceData = useMemo(
    () =>
      (dashboard?.priceHistory ?? [])
        .filter((e: IProductDashboardPriceEntry) => e.priceWithoutTax > 0)
        .slice()
        .sort((a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix())
        .map((e: IProductDashboardPriceEntry) => ({
          date: dayjs(e.createdAt).format(EDateFormat.DAY_COMPACT),
          price: e.priceWithoutTax,
          currency: e.currencyType.isoCode,
        })),
    [dashboard?.priceHistory],
  );

  const priceStats = useMemo(() => {
    if (priceData.length === 0) {
      return null;
    }
    const minPrice = Math.min(...priceData.map((d) => d.price));
    const maxPrice = Math.max(...priceData.map((d) => d.price));
    const minEntry = priceData.find((d) => d.price === minPrice)!;
    const maxEntry = priceData.find((d) => d.price === maxPrice)!;
    const spread = minPrice > 0 ? ((maxPrice - minPrice) / minPrice) * 100 : 0;
    return { minPrice, maxPrice, minEntry, maxEntry, spread };
  }, [priceData]);

  return (
    <Spin spinning={inProcess}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACING.md,
          marginBottom: SPACING.md,
        }}
      >
        <Radio.Group
          buttonStyle="solid"
          value={daysInterval}
          onChange={(e) => setDaysInterval(e.target.value as DayInterval)}
          size="small"
        >
          {DAY_OPTIONS.map((d) => (
            <Radio.Button key={d} value={d}>
              {d}d
            </Radio.Button>
          ))}
        </Radio.Group>

        <Tooltip title="Reload dashboard">
          <Button
            size="small"
            onClick={() => load(daysInterval)}
            disabled={inProcess}
          >
            ↺
          </Button>
        </Tooltip>
      </div>

      <div style={{ display: 'flex', gap: SPACING.lg, flexWrap: 'wrap' }}>
        {(dashboard?.scoring ?? [])
          .filter(
            (row: IProductDashboardScoring) => row.attractivenessScore > 0,
          )
          .map((row: IProductDashboardScoring) => (
            <div
              key={row.countryType.isoCode}
              style={{
                border: `1px solid ${COLOR.borderDefault}`,
                borderRadius: 6,
                padding: `${SPACING.sm}px ${SPACING.md}px`,
                minWidth: 180,
              }}
            >
              <Text
                strong
                style={{ display: 'block', marginBottom: SPACING.xs }}
              >
                {getFlag({ countryTypeIsoCode: row.countryType.isoCode })}{' '}
                {row.countryType.isoCode}
              </Text>

              <div
                style={{
                  display: 'flex',
                  gap: SPACING.xl,
                  marginBottom: SPACING.xs,
                }}
              >
                <Statistic
                  title="Views"
                  value={row.views}
                  styles={{ content: { fontSize: FONT_SIZE.md } }}
                />
                <Statistic
                  title="Cart"
                  value={row.basketCount}
                  styles={{ content: { fontSize: FONT_SIZE.md } }}
                />
                <Statistic
                  title="Orders"
                  value={row.orderCount}
                  styles={{ content: { fontSize: FONT_SIZE.md } }}
                />
              </div>

              <Tooltip
                title={`Attractiveness: ${row.attractivenessScore.toFixed(1)} / 100`}
              >
                <Progress
                  percent={Math.round(row.attractivenessScore)}
                  size="small"
                  status={
                    row.attractivenessScore >= 50 ? 'normal' : 'exception'
                  }
                />
              </Tooltip>
            </div>
          ))}
      </div>

      {dashboard && (
        <div
          style={{
            marginTop: SPACING.lg,
            borderTop: `1px solid ${COLOR.borderDefault}`,
            paddingTop: SPACING.md,
          }}
        >
          <Text
            type="secondary"
            style={{
              fontSize: FONT_SIZE.sm,
              display: 'block',
              marginBottom: SPACING.sm,
            }}
          >
            Web Analytics (Search Console)
          </Text>
          <WebAnalyticsSection entries={dashboard.webAnalytics ?? []} />
          {dashboard.webAnalyticsHistory ? (
            <div style={{ marginTop: SPACING.md }}>
              <EntityHistory history={dashboard.webAnalyticsHistory} />
            </div>
          ) : null}
        </div>
      )}

      {priceData.length > 0 && priceStats && (
        <div style={{ marginTop: SPACING.lg }}>
          <Text type="secondary" style={{ fontSize: FONT_SIZE.sm }}>
            EUR price history (excl. VAT)
          </Text>

          <div
            style={{
              display: 'flex',
              gap: SPACING.lg,
              alignItems: 'center',
              marginTop: SPACING.xs,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: SPACING.sm,
                flexShrink: 0,
              }}
            >
              <div>
                <Statistic
                  title="Min"
                  value={priceStats.minPrice.toFixed(2)}
                  suffix="EUR"
                  styles={{
                    content: { fontSize: FONT_SIZE.md, color: COLOR.success },
                  }}
                />
                <Text type="secondary" style={{ fontSize: FONT_SIZE.xs }}>
                  {priceStats.minEntry.date}
                </Text>
              </div>
              <div>
                <Statistic
                  title="Max"
                  value={priceStats.maxPrice.toFixed(2)}
                  suffix="EUR"
                  styles={{
                    content: { fontSize: FONT_SIZE.md, color: COLOR.error },
                  }}
                />
                <Text type="secondary" style={{ fontSize: FONT_SIZE.xs }}>
                  {priceStats.maxEntry.date}
                </Text>
              </div>
              <div>
                <Statistic
                  title="Spread"
                  value={priceStats.spread.toFixed(1)}
                  suffix="%"
                  styles={{ content: { fontSize: FONT_SIZE.md } }}
                />
              </div>
            </div>

            <ResponsiveContainer width="100%" height={120}>
              <LineChart
                data={priceData}
                margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={COLOR.borderDefault}
                />
                <XAxis dataKey="date" tick={{ fontSize: FONT_SIZE.xs }} />
                <YAxis
                  tick={{ fontSize: FONT_SIZE.xs }}
                  domain={['auto', 'auto']}
                  tickFormatter={(v: number) => v.toFixed(2)}
                  width={50}
                />
                <RechartsTooltip
                  content={<PriceChartTooltip />}
                  cursor={{
                    stroke: COLOR.primary,
                    strokeWidth: 1,
                    strokeDasharray: '4 2',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke={COLOR.primary}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5, strokeWidth: 0 }}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </Spin>
  );
}
